import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Table.module.scss';
import { PmrClassificationComponent } from '../../../Util/PmrClassification/PmrClassification';
import { PmrPrintValue } from '../../../Util/PmrPrintValue/PmrPrintValue';
import { Pagination } from '@material-ui/lab';
import '../../../Util/Pagination/Pagination.scss';
import NumberFormat from 'react-number-format';
import { showIndexTCDI } from '../../../../services/booleanUtil';
import { ProjectProductTypeClassification } from '../../../../models/ProjectProductTypeClassification';
import { TcdiClassificationComponent } from '../../../Util/TcdiClassification/TcdiClassification';
import { CriteriaType } from '../../../../models/PaginationIndicator';
import { useHistory } from 'react-router-dom';
import Project from '../../../../models/Project';
import { trackActionClicked } from '../../../../api/trackActionClickedApi';
import { TrackedActions } from '../../../../models/TrackedActionsEnum';
import { useCountryTableRows } from './useCountryTableRows';
import { handleTcdiValueOnTcdiComponent } from './Util';
import { ExcelDownloadButton } from '../../../Util/ExcelDownloadButton/ExcelDownloadButton';
import { Tooltip } from '@material-ui/core';
import useDataNormalize from '../../../../hooks/useDataNormalize';
import PaginationTable from '../../PaginationTable/PaginationTable';
import { withStyles } from '@material-ui/core/styles';
import { FilterTables } from '../../../Filter/FilterTables/FilterTables';
import FiltersErrorMessage from '../../../Util/Errors/FiltersErrorMessage/FiltersErrorMessage';
import SearchTabErrorMessage from '../../../Util/Errors/SearchTabErrorMessage/SearchTabErrorMessage';

const BlackTooltip = withStyles({
  tooltip: {
    //   color: '$ci-color-greyscale-90',
    backgroundColor: '#101319',
    fontSize: '16px',
    fontFamily: 'Rubik',
    fontWeight: 400,
    lineHeight: '20px',
    padding: '8px 12px',
    borderRadius: '8px',
  },
  arrow: {
    fontSize: 20,
    color: '#4A4A4A',
    '&::before': {
      backgroundColor: '#101319',
    },
  },
})(Tooltip);

type Props = {
  projects: Project[];
};

const headers = ['id', 'last_project', 'last_operation', 'name', 'total_approved_amount_usd', 'PMR/tcdi'];

const selectedKeys = ['id', 'code', 'loanNumber', 'name', 'financialInformation_totalDisbursementAmount', 'pmrValue'];

export const Table = ({ projects }: Props) => {
  const { t } = useTranslation();
  const history = useHistory();

  const {
    container,
    tableHeader,
    tableRow,
    rowValue,
    rowList,
    rowValueName,
    headerTitleClickable,
    borderTable,
    iconTex,
  } = styles;
  const {
    dataFilter,
    currentList,
    currentPage,
    pagesCount,
    rowsPerPage,
    searchTerm,
    hasActiveSearch,
    handleSearchChange,
    handleRowsPerPageChange,
    handlePaginationChange,
    handleSort,
  } = useCountryTableRows(projects, selectedKeys);
  const data = useDataNormalize(headers, selectedKeys, dataFilter);

  const handleClick = (project: Project) => {
    history.push('/projectdetail?id=' + project.id);
    trackActionClicked(TrackedActions.projectDetailsFromListClicked);
  };

  return (
    <div className={container}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <ExcelDownloadButton headers={headers} selectedKeys={selectedKeys} dataExcel={data} />
        <FilterTables
          projects={projects}
          keysFilter={selectedKeys}
          disabledNumber={currentPage}
          indicatorKey={'PORTFOLIO_OVERVIEW_INCLUDING_REGIONAL_PROJECTS_BY_COUNTRY'}
          onSearchChange={handleSearchChange}
        />
      </div>

      <div className={borderTable}>
        <div className={tableHeader}>
          {dataFilter?.length === 0 ? (
            <div className="text-head">
              <p></p>
            </div>
          ) : (
            <>
              <h4 className={headerTitleClickable} onClick={() => handleSort('code', CriteriaType.string)}>
                {t('project')}
              </h4>
              <h4 className={headerTitleClickable} onClick={() => handleSort('loanNumber', CriteriaType.string)}>
                {t('operation')}
              </h4>
              <h4 className={headerTitleClickable} onClick={() => handleSort('name', CriteriaType.string)}>
                {t('project_name')}
              </h4>
              <h4
                className={headerTitleClickable}
                onClick={() => handleSort('totalDisbursementAmount', CriteriaType.nested, 'financialInformation')}
              >
                {`${t('total_approved_amount_usd')} ${t('dolar_sign_parentesis')}`}
              </h4>
              <h4
                className={headerTitleClickable}
                onClick={() => handleSort('tcdiPmrValue', CriteriaType.decimalNumber)}
              >
                {`${t('pmr_initials')}/${t('tcdi_initials')}`}
              </h4>
            </>
          )}
        </div>
        <ul className={rowList}>
          {dataFilter?.length === 0
            ? (
              <li className="table-empty-message-cell">
                {projects.length > 0 && hasActiveSearch ? (
                  <SearchTabErrorMessage search={searchTerm} />
                ) : (
                  <FiltersErrorMessage isTable={true} />
                )}
              </li>
            )
            : currentList?.map(project => (
                <li className={tableRow} key={`${project.loanNumber}${project.code}`}>
                  <BlackTooltip title={t('go_to_project_info') || ''} arrow>
                    <p className={`${rowValue} link-project`} onClick={() => handleClick(project)}>
                      {project.code}
                    </p>
                  </BlackTooltip>
                  <p className={rowValue}>{project.loanNumber}</p>
                  <BlackTooltip title={project.name || ''} placement="bottom" enterDelay={500} leaveDelay={100} arrow>
                    <p className={rowValueName}>{project.name}</p>
                  </BlackTooltip>
                  <p className={rowValue}>
                    <NumberFormat
                      value={project.financialInformation.totalDisbursementAmount}
                      displayType={'text'}
                      thousandSeparator={'.'}
                      decimalSeparator={','}
                    />
                  </p>
                  <div className={rowValue}>
                    {showIndexTCDI(
                      project.projectProductType,
                      project &&
                        project.projectProductTypeClassification &&
                        project.projectProductTypeClassification.code ===
                          ProjectProductTypeClassification.IGR_GREATER_THAN_3MM,
                    ) ? (
                      <>
                        <div className={iconTex}>
                          <TcdiClassificationComponent
                            clasification={handleTcdiValueOnTcdiComponent(project.tcdiValue)}
                          />
                          <span className="pmr-classification-margin-left">
                            {project.tcdiValue === null ? t('not_assigned_initials') : project.tcdiValue}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={iconTex}>
                          <PmrClassificationComponent classificationColor={project.pmrClassification} />
                          <span className="pmr-classification-margin-left">
                            <PmrPrintValue pmrClassi={project.pmrClassification} pmrValue={project.pmrValue} />
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </li>
              ))}
        </ul>
      </div>
      {dataFilter.length > 0 && (
        <div className="pagination-style">
          <PaginationTable fnSetRows={handleRowsPerPageChange} rows={rowsPerPage} numProyects={dataFilter.length} />
          <Pagination count={pagesCount} page={currentPage} onChange={handlePaginationChange} />
        </div>
      )}
    </div>
  );
};
